import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import * as WebBrowser from 'expo-web-browser';
import { useSQLiteContext } from 'expo-sqlite';
import {
  Camera,
  ChevronRight,
  FileText,
  Image as ImageIcon,
  Paperclip,
  Plus,
  ShieldCheck,
  Trash2,
} from 'lucide-react-native';
import { useEffect, useState } from 'react';
import {
  Image,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';

import { useConfirm } from '@/components/ConfirmDialog';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import type { DocumentKind, DocumentRow } from '@/db/types';
import { parseLocalDate } from '@/lib/dates';
import {
  DOCUMENT_KIND_LABEL,
  addDocument,
  deleteDocument,
  listDocumentsForSystem,
} from '@/lib/documents';
import { isImageMime, isPdfMime, persistPickedFile } from '@/lib/files';

const KIND_ICONS: Record<DocumentKind, React.ComponentType<{ size?: number; color?: string; strokeWidth?: number }>> = {
  manual: FileText,
  warranty: ShieldCheck,
  receipt: FileText,
  photo: ImageIcon,
  insurance: ShieldCheck,
  permit: FileText,
  inspection: FileText,
  invoice: FileText,
  estimate: FileText,
  other: Paperclip,
};

const KIND_OPTIONS: { kind: DocumentKind; hint: string }[] = [
  { kind: 'manual', hint: 'Owner\'s manual or quick-start' },
  { kind: 'warranty', hint: 'Coverage period + claim contact' },
  { kind: 'receipt', hint: 'Proof of purchase' },
  { kind: 'invoice', hint: 'Service bill or repair invoice' },
  { kind: 'inspection', hint: 'Inspection report' },
  { kind: 'permit', hint: 'Building or trade permit' },
  { kind: 'insurance', hint: 'Insurance policy or claim' },
  { kind: 'photo', hint: 'Photo of the unit or its data plate' },
  { kind: 'other', hint: 'Anything that doesn\'t fit above' },
];

export function DocumentsList({
  propertyId,
  systemId,
}: {
  propertyId: string;
  systemId: string;
}) {
  const db = useSQLiteContext();
  const confirm = useConfirm();
  const [docs, setDocs] = useState<DocumentRow[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [stage, setStage] = useState<'idle' | 'kind' | 'meta'>('idle');
  const [pending, setPending] = useState<{
    file_uri: string;
    file_name: string;
    mime_type: string | null;
    file_size: number | null;
  } | null>(null);
  const [chosenKind, setChosenKind] = useState<DocumentKind>('manual');
  const [title, setTitle] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  useEffect(() => {
    refresh();
  }, [systemId]);

  const refresh = async () => {
    setDocs(await listDocumentsForSystem(db, systemId));
  };

  const pickFile = async () => {
    setPickerOpen(false);
    try {
      const res = await DocumentPicker.getDocumentAsync({
        copyToCacheDirectory: true,
        multiple: false,
      });
      if (res.canceled || !res.assets || res.assets.length === 0) return;
      const a = res.assets[0];
      const persisted = await persistPickedFile(a.uri, a.name);
      setPending({
        file_uri: persisted,
        file_name: a.name,
        mime_type: a.mimeType ?? null,
        file_size: a.size ?? null,
      });
      setTitle(a.name.replace(/\.[a-zA-Z0-9]+$/, ''));
      setStage('kind');
    } catch (err) {
      console.warn('document pick failed', err);
    }
  };

  const pickPhoto = async () => {
    setPickerOpen(false);
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) return;
    const res = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.7,
    });
    if (res.canceled || !res.assets[0]) return;
    const a = res.assets[0];
    const persisted = await persistPickedFile(a.uri, a.fileName ?? `photo-${Date.now()}.jpg`);
    setPending({
      file_uri: persisted,
      file_name: a.fileName ?? 'Photo',
      mime_type: a.mimeType ?? 'image/jpeg',
      file_size: a.fileSize ?? null,
    });
    setTitle('Photo');
    setChosenKind('photo');
    setStage('kind');
  };

  const takePhoto = async () => {
    setPickerOpen(false);
    const perm = await ImagePicker.requestCameraPermissionsAsync();
    if (!perm.granted) return;
    const res = await ImagePicker.launchCameraAsync({ quality: 0.7 });
    if (res.canceled || !res.assets[0]) return;
    const a = res.assets[0];
    const persisted = await persistPickedFile(a.uri, `camera-${Date.now()}.jpg`);
    setPending({
      file_uri: persisted,
      file_name: 'Camera photo',
      mime_type: 'image/jpeg',
      file_size: a.fileSize ?? null,
    });
    setTitle('Camera photo');
    setChosenKind('photo');
    setStage('kind');
  };

  const finalize = async () => {
    if (!pending) return;
    const expiresMs = expiresAt
      ? parseLocalDate(expiresAt.replace(/\//g, '-')) || null
      : null;
    await addDocument(db, {
      propertyId,
      systemId,
      kind: chosenKind,
      title: title.trim() || pending.file_name,
      file_uri: pending.file_uri,
      mime_type: pending.mime_type,
      file_size: pending.file_size,
      expires_at: expiresMs && !isNaN(expiresMs) ? expiresMs : null,
    });
    setPending(null);
    setTitle('');
    setExpiresAt('');
    setStage('idle');
    await refresh();
  };

  const openDoc = async (d: DocumentRow) => {
    try {
      if (Platform.OS === 'web') {
        window.open(d.file_uri, '_blank');
      } else {
        await WebBrowser.openBrowserAsync(d.file_uri);
      }
    } catch (err) {
      console.warn('open document failed', err);
    }
  };

  const remove = async (d: DocumentRow) => {
    const ok = await confirm({
      title: 'Remove document?',
      message: `“${d.title}” will be deleted from this system.`,
      destructive: true,
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    await deleteDocument(db, d.id);
    await refresh();
  };

  return (
    <View style={styles.wrap}>
      <View style={styles.head}>
        <Text style={styles.title}>Documents · {docs.length}</Text>
        <Pressable
          onPress={() => setPickerOpen(true)}
          style={({ pressed }) => [styles.addBtn, pressed && { transform: [{ scale: 0.96 }] }]}>
          <Plus size={14} color={Palette.cream} strokeWidth={2.2} />
          <Text style={styles.addBtnText}>Add</Text>
        </Pressable>
      </View>

      {docs.length === 0 ? (
        <Text style={styles.empty}>
          No manuals, warranties, or receipts yet. Adding them here means
          insurance, resale, or service claims are a single tap away later.
        </Text>
      ) : (
        <View style={{ gap: 8, marginTop: 10 }}>
          {docs.map((d) => {
            const Icon = KIND_ICONS[d.kind] ?? Paperclip;
            return (
              <Pressable
                key={d.id}
                onPress={() => openDoc(d)}
                style={({ pressed }) => [styles.row, pressed && { transform: [{ scale: 0.99 }] }]}>
                {isImageMime(d.mime_type) ? (
                  <Image source={{ uri: d.file_uri }} style={styles.thumb} />
                ) : (
                  <View style={styles.iconBox}>
                    <Icon size={22} color={Palette.forest} strokeWidth={1.7} />
                  </View>
                )}
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.docTitle} numberOfLines={1}>{d.title}</Text>
                  <Text style={styles.docMeta} numberOfLines={1}>
                    {DOCUMENT_KIND_LABEL[d.kind]}
                    {d.expires_at ? ` · expires ${new Date(d.expires_at).toLocaleDateString()}` : ''}
                    {isPdfMime(d.mime_type) ? ' · PDF' : ''}
                  </Text>
                </View>
                <Pressable
                  onPress={() => remove(d)}
                  hitSlop={8}
                  style={({ pressed }) => [styles.trash, pressed && { opacity: 0.6 }]}>
                  <Trash2 size={15} color={Palette.clay} strokeWidth={1.7} />
                </Pressable>
              </Pressable>
            );
          })}
        </View>
      )}

      {/* Source picker modal */}
      <Modal animationType="fade" transparent visible={pickerOpen} onRequestClose={() => setPickerOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setPickerOpen(false)}>
          <View style={styles.sheet}>
            <Text style={styles.sheetTitle}>Add a document</Text>
            <Pressable onPress={pickFile} style={({ pressed }) => [styles.sheetOption, pressed && { opacity: 0.85 }]}>
              <Paperclip size={20} color={Palette.forest} strokeWidth={1.7} />
              <Text style={styles.sheetOptionText}>Pick a file (PDF, doc, image)</Text>
            </Pressable>
            <Pressable onPress={pickPhoto} style={({ pressed }) => [styles.sheetOption, pressed && { opacity: 0.85 }]}>
              <ImageIcon size={20} color={Palette.forest} strokeWidth={1.7} />
              <Text style={styles.sheetOptionText}>Pick from photos</Text>
            </Pressable>
            {Platform.OS !== 'web' ? (
              <Pressable onPress={takePhoto} style={({ pressed }) => [styles.sheetOption, pressed && { opacity: 0.85 }]}>
                <Camera size={20} color={Palette.forest} strokeWidth={1.7} />
                <Text style={styles.sheetOptionText}>Take a photo</Text>
              </Pressable>
            ) : null}
          </View>
        </Pressable>
      </Modal>

      {/* Kind + metadata modal */}
      <Modal animationType="fade" transparent visible={stage !== 'idle'} onRequestClose={() => setStage('idle')}>
        <Pressable style={styles.backdrop} onPress={() => setStage('idle')}>
          <Pressable style={styles.sheetWide} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.sheetTitle}>What kind of document?</Text>
            <View style={{ gap: 6 }}>
              {KIND_OPTIONS.map((opt) => {
                const sel = chosenKind === opt.kind;
                return (
                  <Pressable
                    key={opt.kind}
                    onPress={() => setChosenKind(opt.kind)}
                    style={[styles.kindRow, sel && styles.kindRowSel]}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.kindLabel}>{DOCUMENT_KIND_LABEL[opt.kind]}</Text>
                      <Text style={styles.kindHint}>{opt.hint}</Text>
                    </View>
                    {sel ? <ChevronRight size={16} color={Palette.forest} strokeWidth={2} /> : null}
                  </Pressable>
                );
              })}
            </View>

            <View style={{ marginTop: 14 }}>
              <Text style={styles.fieldLabel}>TITLE</Text>
              <TextInput
                value={title}
                onChangeText={setTitle}
                placeholder="e.g. Furnace manual"
                placeholderTextColor={Palette.inkFaint}
                style={styles.fieldInput}
              />
            </View>

            {chosenKind === 'warranty' || chosenKind === 'insurance' ? (
              <View style={{ marginTop: 12 }}>
                <Text style={styles.fieldLabel}>EXPIRES (OPTIONAL)</Text>
                <TextInput
                  value={expiresAt}
                  onChangeText={setExpiresAt}
                  placeholder="YYYY-MM-DD"
                  placeholderTextColor={Palette.inkFaint}
                  style={styles.fieldInput}
                />
              </View>
            ) : null}

            <Pressable
              onPress={finalize}
              style={({ pressed }) => [styles.saveBtn, pressed && { transform: [{ scale: 0.98 }] }]}>
              <Text style={styles.saveText}>Save document</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.xl,
    padding: Spacing.four,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.inkFaint,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: Palette.forest,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
  },
  addBtnText: {
    color: Palette.cream,
    fontFamily: Fonts.sansBold,
    fontSize: 12.5,
    letterSpacing: 0.2,
  },
  empty: {
    marginTop: 10,
    fontFamily: Fonts.sansMedium,
    fontSize: 13.5,
    color: Palette.inkSoft,
    lineHeight: 19,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
    backgroundColor: Palette.sand,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: 14,
    padding: 11,
  },
  thumb: {
    width: 38,
    height: 38,
    borderRadius: 11,
  },
  iconBox: {
    width: 38,
    height: 38,
    borderRadius: 11,
    backgroundColor: '#EAF0EA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  docTitle: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  docMeta: { fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft, marginTop: 2 },
  trash: { padding: 4 },

  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  sheet: {
    backgroundColor: Palette.sand,
    borderRadius: Radius.xl,
    padding: Spacing.four,
    width: '100%',
    maxWidth: 360,
    gap: 8,
  },
  sheetWide: {
    backgroundColor: Palette.sand,
    borderRadius: Radius.xl,
    padding: Spacing.four,
    width: '100%',
    maxWidth: 420,
  },
  sheetTitle: {
    fontFamily: Fonts.serifBold,
    fontSize: 18,
    color: Palette.ink,
    marginBottom: 8,
  },
  sheetOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
    paddingVertical: 14,
    paddingHorizontal: 14,
    backgroundColor: Palette.card,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Palette.line,
  },
  sheetOptionText: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.ink,
  },
  kindRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
  },
  kindRowSel: {
    borderColor: Palette.forest,
    backgroundColor: '#EEF3EE',
  },
  kindLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.ink,
  },
  kindHint: {
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkSoft,
    marginTop: 1,
  },
  fieldLabel: {
    fontSize: 12,
    fontFamily: Fonts.sansBold,
    color: Palette.inkSoft,
    letterSpacing: 0.4,
  },
  fieldInput: {
    marginTop: 5,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    borderRadius: 11,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontFamily: Fonts.sansSemibold,
    fontSize: 14,
    color: Palette.ink,
  },
  saveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Palette.forest,
    paddingVertical: 14,
    borderRadius: 14,
    marginTop: 14,
  },
  saveText: {
    color: Palette.cream,
    fontFamily: Fonts.sansBold,
    fontSize: 14.5,
  },
});
